import { Link, useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect, MouseEvent } from 'react';
import { docsTree, DocNode } from './docsIndex';
import './docsSidebar.css';

const DOCS_BASE = '/documentation';

function hrefForSlug(slug: string) {
  return slug ? `${DOCS_BASE}/${slug}` : DOCS_BASE;
}

// "a/b/c" -> ["a", "a/b"]
function ancestorsOf(slug: string) {
  const parts = slug.split('/').filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < parts.length - 1; i++) out.push(parts.slice(0, i + 1).join('/'));
  return new Set(out);
}

function findNode(nodes: DocNode[], slug: string): DocNode | null {
  for (const n of nodes) {
    if ((n.slug || '') === slug) return n;
    if (n.children?.length) {
      const hit = findNode(n.children, slug);
      if (hit) return hit;
    }
  }
  return null;
}

function Row({
  node,
  currentSlug,
  expanded,
  setExpanded,
  depth = 0,
}: {
  node: DocNode;
  currentSlug: string;
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  depth?: number;
}) {
  const key = node.slug || '__root__';
  const hasChildren = !!node.children?.length;
  const isOpen = !!expanded[key];
  const isCurrent = currentSlug === node.slug;
  const isActiveBranch = node.slug ? currentSlug === node.slug || currentSlug.startsWith(node.slug + '/') : currentSlug === '';

  // Open parent immediately when its link is clicked (so children show)
  const handleParentLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!hasChildren) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    setExpanded((m) => ({ ...m, [key]: true }));
  };

  return (
    <li className="ds-item" data-depth={depth}>
      <div className={`ds-parent-row ${isActiveBranch ? 'is-active' : ''}`}>
        {hasChildren ? (
          <button
            type="button"
            className="ds-caret"
            aria-label={isOpen ? 'Collapse section' : 'Expand section'}
            aria-expanded={isOpen}
            onClick={() => setExpanded((m) => ({ ...m, [key]: !m[key] }))}
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M7 5l6 5-6 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <span className="ds-caret ds-caret--spacer" aria-hidden="true" />
        )}

        <Link
          to={hrefForSlug(node.slug || '')}
          onClick={handleParentLinkClick}
          className={`ds-parent-link ${isCurrent ? 'is-current' : ''}`}
          aria-current={isCurrent ? 'page' : undefined}
          title={node.title}
        >
          {node.title}
        </Link>
      </div>

      {hasChildren && node.children && (
        <ul className="ds-children" data-expanded={isOpen}>
          {node.children.map((child) => (
            <Row key={child.slug} node={child} currentSlug={currentSlug} expanded={expanded} setExpanded={setExpanded} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function DocsSidebar() {
  const { pathname } = useLocation(); // e.g. "/documentation/test/subfolder/grandchild"
  const slug = pathname.startsWith(DOCS_BASE) ? pathname.slice(DOCS_BASE.length).replace(/^\/+/, '') : '';

  // Open ancestors + the current node (if it has children). Not deeper descendants.
  const initialExpanded = useMemo(() => {
    const open: Record<string, boolean> = {};
    ancestorsOf(slug).forEach((s) => {
      open[s || '__root__'] = true;
    });
    const current = findNode(docsTree, slug);
    if (current?.children?.length) open[slug || '__root__'] = true;
    return open;
  }, [slug]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialExpanded);
  useEffect(() => setExpanded(initialExpanded), [initialExpanded]);

  // MOBILE drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer when the route changes
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line
  }, [pathname]);

  // Escape key closes mobile drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* Mobile open button (hidden on desktop via CSS) */}
      <button
        type="button"
        className="ds-open-btn"
        aria-label="Open documentation menu"
        aria-haspopup="true"
        aria-expanded={mobileOpen}
        aria-controls="docs-sidebar-panel"
        onClick={() => setMobileOpen(true)}
      >
        <span className="ds-open-btn__icon" aria-hidden="true">
          ☰
        </span>
        <span className="ds-open-btn__label">Docs</span>
      </button>

      {/* Overlay for mobile */}
      <button type="button" className={`ds-overlay${mobileOpen ? ' is-open' : ''}`} aria-label="Close menu" onClick={() => setMobileOpen(false)} />

      <nav id="docs-sidebar-panel" className={`docs-sidebar${mobileOpen ? ' is-mobile-open' : ''}`} aria-label="Documentation">
        <div className="ds-header">
          <span className="ds-title">Documentation</span>

          {/* Close button (mobile only) */}
          <button type="button" className="ds-close" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
            ✕
          </button>
        </div>

        <ul className="ds-list">
          {docsTree.map((node) => (
            <Row key={node.slug || '__root__'} node={node} currentSlug={slug} expanded={expanded} setExpanded={setExpanded} />
          ))}
        </ul>
      </nav>
    </>
  );
}
